import { type AskPunttProjectData } from "@mg/schemas/src/christo/askPuntt";
import { BellRinging, Check } from "@phosphor-icons/react";
import { Button, Callout, Flex, Text } from "@radix-ui/themes";
import { useEffect, useState } from "react";

import { ReviewStatusCallout } from "./ReviewStatusCallout";

interface ProjectReviewStatusBannerProps {
  projectData: AskPunttProjectData;
  /**
   * True while the Vitalora killer-flow typewriter is still revealing
   * the parallel review. Forces the banner into the "AI review in
   * progress" yellow state regardless of the stages payload — the
   * payload lands fully resolved (some stages `done`, some
   * `needs_review`) before the user has visibly seen any reviewer
   * run, so showing "Reg needs human review — Notify Reviewers"
   * during the typewriter would give the result away ahead of the
   * theatrical reveal. Cleared automatically when streaming
   * completes; the banner then falls through to the normal stages-
   * driven branches below.
   */
  isKillerFlowStreaming?: boolean;
}

/**
 * Status banner that sits above the chat composer reflecting where the
 * project review is in its lifecycle:
 *
 *   * killer-flow streaming      → yellow "AI review in progress" (forced)
 *   * active stages              → yellow "AI review in progress"
 *   * needs_review + not notified → red "X needs human review — Notify"
 *   * needs_review + notified    → green "Notifications have been sent"
 *
 * The notify state is held locally (resets whenever the underlying needs-
 * review set changes — e.g. a new round flips Regulatory back to
 * needs_review). It was previously implicit on `hasNeedsReview && !allDone`,
 * which made the green stripe lie: it claimed notifications had gone out
 * the moment stages flipped, with no actual action having been taken.
 */
export function ProjectReviewStatusBanner({
  projectData,
  isKillerFlowStreaming,
}: ProjectReviewStatusBannerProps) {
  const stages = projectData.stages ?? [];
  const hasActive = stages.some((s) => s.status === "active");
  const hasNeedsReview = stages.some((s) => s.status === "needs_review");
  const allDone = stages.every((s) => s.status === "done");

  const needsReviewKey = stages
    .filter((s) => s.status === "needs_review")
    .map((s) => s.name ?? "")
    .join("|");

  const [notified, setNotified] = useState(false);
  // Reset the notified flag whenever the needs-review set changes so a new
  // round (e.g. Regulatory re-fires after a revision) re-prompts the user.
  useEffect(() => {
    setNotified(false);
  }, [needsReviewKey]);

  if (stages.length === 0) return null;

  if (hasActive || isKillerFlowStreaming) {
    return (
      <ReviewStatusCallout
        color="blue"
        testid="project-review-banner-active"
        icon={
          <span className="bg-blue-9 size-2.5 animate-pulse rounded-full motion-reduce:animate-none" />
        }
      >
        <Callout.Text weight="medium">AI review in progress</Callout.Text>
        <Text as="p" size="1" color="gray" className="mt-0.5">
          AskPuntt will update you on which stages can be skipped (no human
          needed) and which need human review before the project can progress.
        </Text>
      </ReviewStatusCallout>
    );
  }

  if (hasNeedsReview && !allDone) {
    if (notified) {
      return (
        <ReviewStatusCallout
          color="green"
          testid="project-review-banner-notified"
          icon={<Check size={16} weight="bold" />}
        >
          <Callout.Text weight="medium">
            Notifications have been sent to reviewers who need to complete their
            review before the project can progress.
          </Callout.Text>
        </ReviewStatusCallout>
      );
    }

    const needsReviewNames = stages
      .filter((s) => s.status === "needs_review")
      .map((s) => s.name ?? "Stage");
    const reviewerLabel =
      needsReviewNames.length === 1
        ? `${needsReviewNames[0]} needs human review`
        : `${needsReviewNames.join(" & ")} need human review`;

    return (
      <ReviewStatusCallout
        color="amber"
        testid="project-review-banner-needs-review"
        icon={<BellRinging size={16} weight="fill" />}
      >
        <Flex align="center" gap="3" wrap="wrap">
          <Callout.Text className="min-w-0 flex-1">
            {reviewerLabel} — notify reviewers so the project can progress.
          </Callout.Text>
          <Button
            size="2"
            variant="solid"
            color="amber"
            className="shrink-0 cursor-pointer"
            data-testid="project-review-banner-notify-btn"
            onClick={() => setNotified(true)}
          >
            <BellRinging size={14} weight="fill" aria-hidden="true" />
            Notify Reviewers
          </Button>
        </Flex>
      </ReviewStatusCallout>
    );
  }

  return null;
}
